import type { ScreenName } from './screen';

//TODO: Create a documentation page in storybook for these things.

export type ResponsiveValue<T> = T | [T, ScreenNameIndexedValues<T>?];

type ScreenNameWithInitial = ScreenName | 'initial';

type ScreenNameIndexedValues<T> = {
  [screenName in ScreenName]?: T;
};

type OptionalScreenNameIndexedValuesWithInitial<T> = {
  [screenName in ScreenNameWithInitial]: T | undefined;
};

type ScreenNameIndexedValuesWithInitial<T> = {
  [screenName in ScreenNameWithInitial]: T;
};

// export type ResponsiveValueClassMap<T extends string | number> = {
//   [key in ScreenNameWithInitial]?: Record<T, string | undefined> | ((value: T) => string | undefined)
// }

/**
 * Responsive value class map.
 * Provides a mapping for each screen name to a responsive value or a function that resolves the value.
 * You can use this to create responsive styles based on the screen size when you want to avoid any hook based solutions.
 * This is a more complex approach than using a simple responsive value, but it enables server side rendering and avoids any layout shifts based on initial screen name resolution.
 */
export type ResponsiveValueClassMap<T extends string | number> = Record<
  ScreenNameWithInitial,
  Record<T, string | undefined> | ((value: T) => string | undefined)
>;

/**
 * Resolves given responsive property value to screen name indexed values including an initial key for the initial screen.
 */
export const getScreenIndexedResponsiveValue = <T>(
  propValue: ResponsiveValue<T> | undefined,
  defaultValue: T,
): OptionalScreenNameIndexedValuesWithInitial<T> => {
  if (typeof propValue === 'undefined' || !Array.isArray(propValue)) {
    return {
      initial: propValue ?? defaultValue,
      xs: undefined,
      sm: undefined,
      md: undefined,
      lg: undefined,
      xl: undefined,
      xxl: undefined,
    };
  }

  const [initial, screenNameIndexedValues] = propValue;

  if (typeof screenNameIndexedValues === 'undefined') {
    return {
      initial,
      xs: undefined,
      sm: undefined,
      md: undefined,
      lg: undefined,
      xl: undefined,
      xxl: undefined,
    };
  }

  return {
    initial,
    xs: screenNameIndexedValues.xs ?? undefined,
    sm: screenNameIndexedValues.sm ?? undefined,
    md: screenNameIndexedValues.md ?? undefined,
    lg: screenNameIndexedValues.lg ?? undefined,
    xl: screenNameIndexedValues.xl ?? undefined,
    xxl: screenNameIndexedValues.xxl ?? undefined,
  };
};

/**
 * Resolves given responsive property value to screen name indexed values.
 */
export const getScreenIndexedResponsiveValueForEachScreen = <T>(
  propValue: ResponsiveValue<T> | undefined,
  defaultValue: T,
): ScreenNameIndexedValuesWithInitial<T> => {
  const screenNameIndexedValues = getScreenIndexedResponsiveValue(
    propValue,
    defaultValue,
  );

  const screens: Record<ScreenNameWithInitial, T | undefined> = {
    ...screenNameIndexedValues,
  };

  screens.initial = screens.initial ?? defaultValue;
  screens.xs = screens.xs ?? screens.initial;
  screens.sm = screens.sm ?? screens.xs;
  screens.md = screens.md ?? screens.sm;
  screens.lg = screens.lg ?? screens.md;
  screens.xl = screens.xl ?? screens.lg;
  screens.xxl = screens.xxl ?? screens.xl;

  return {
    initial: screens.initial ?? defaultValue,
    xs: screens.xs ?? screens.initial,
    sm: screens.sm ?? screens.xs,
    md: screens.md ?? screens.sm,
    lg: screens.lg ?? screens.md,
    xl: screens.xl ?? screens.lg,
    xxl: screens.xxl ?? screens.xl,
  };
};

/**
 * Get responsive value from prop value.
 */
export const getResponsiveValue = <T>(
  propValue: ResponsiveValue<T> | undefined,
  defaultValue: T,
  screen?: ScreenName,
): T => {
  if (typeof screen !== 'undefined') {
    const screens = getScreenIndexedResponsiveValueForEachScreen(
      propValue,
      defaultValue,
    );

    return screens[screen] ?? defaultValue;
  }

  if (typeof propValue === 'undefined') {
    return defaultValue;
  }

  if (!Array.isArray(propValue)) {
    return propValue;
  }

  return propValue[0];
};

/**
 * Generates css for each responsive value based on the rules from the given screen/value map.
 * See the `Icon` component for a simple example of how to use this or the `IconButton` component for a more complex example.
 */
export const getClassNamesFromMap = <T extends string | number = string>(
  map: ResponsiveValueClassMap<T>,
  value: ResponsiveValue<T> | undefined,
  defaultValue: T,
): string => {
  const screenIndexed = getScreenIndexedResponsiveValue<T>(value, defaultValue);

  const classList = Object.entries(screenIndexed).reduce<string[]>(
    (list, [screenArg, value]) => {
      if (typeof value === 'undefined') {
        return list;
      }

      const screen = screenArg as keyof typeof screenIndexed;
      const resolvedScreenIndexedValues = map[screen];

      if (typeof resolvedScreenIndexedValues === 'undefined') {
        return list;
      }

      const screenValue =
        typeof resolvedScreenIndexedValues === 'function'
          ? resolvedScreenIndexedValues(value)
          : resolvedScreenIndexedValues?.[value];

      if (typeof screenValue === 'undefined') {
        return list;
      }

      return [...list, screenValue];
    },
    [],
  );

  return classList.join(' ');
};
